import { Badge, Box, Stack, Text } from '@chakra-ui/react';
import {
  BackofficeActivitiesOrderItemWithVoucher,
  VOUCHER_REWARD_TYPE,
} from '@diamond/shared/types';
import { PromoPrice } from '@diamond/shared/ui';

import { ColumnComponentProps } from '../../../types/activity-column-types';
import {
  formatCurrency,
  getActivityItemDisplay,
  getPromoAdjustedCheckoutUnitPrice,
  getVoucherAdjustedUnitPrice,
  resolveActivityCheckoutOnlyUnitPrice,
  resolveOrderItemCheckoutStrikethroughBase,
  shouldShowCheckoutUnitPricePlaceholder,
} from '../../../utils/activity-formatters';
import { ActivityDetailStagePlaceholder } from '../../activity-detail/activity-detail-stage-qty-cell';

interface Props
  extends ColumnComponentProps<
    BackofficeActivitiesOrderItemWithVoucher,
    number
  > {
  isVoucherEnabled?: boolean;
  align?: 'left' | 'center' | 'right';
  orderStatus?: string;
}

export function PriceColumn({
  info,
  isVoucherEnabled = false,
  align = 'left',
  orderStatus,
}: Props) {
  const formatCurrencyNoDecimals = (value: number) =>
    `Rp${Math.trunc(value).toLocaleString('id-ID')}`;

  const quantityItem = info.row?.original?.quantity_free_goods;
  const item = info.row?.original;

  // Safe access with fallbacks
  if (!item) {
    return <Text>-</Text>;
  }

  const display = getActivityItemDisplay(item);
  const strikethroughBase = resolveOrderItemCheckoutStrikethroughBase(item);
  const promoAdjustedPrice = getPromoAdjustedCheckoutUnitPrice(item);
  const originalPrice = strikethroughBase;
  let discountAmount: number | undefined = item.deduction_amount;

  if (isVoucherEnabled && item.has_voucher_discount) {
    const voucherResult = getVoucherAdjustedUnitPrice(
      promoAdjustedPrice,
      item,
      isVoucherEnabled
    );
    discountAmount = voucherResult.discountAmount ?? discountAmount;
  }

  // Use voucher_reward_type for explicit check (cleaner than inferring from fields)
  const hasNominalVoucher =
    isVoucherEnabled &&
    item.voucher_reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT;

  const isPromoNominalDiscount =
    info.row.original.is_discount &&
    !item.has_voucher_discount &&
    (discountAmount === undefined || discountAmount === null) &&
    info.row.original.price_after_discount !== undefined &&
    info.row.original.price_after_discount !== originalPrice;

  const promoNominalDiscountAmount = isPromoNominalDiscount
    ? originalPrice - (info.row.original.price_after_discount ?? originalPrice)
    : 0;

  if (
    shouldShowCheckoutUnitPricePlaceholder(
      orderStatus,
      item.checkout_unit_price,
      item.price
    )
  ) {
    return <ActivityDetailStagePlaceholder />;
  }

  if (!display.showPriceSection && !quantityItem) {
    return null;
  }

  const stackAlign =
    align === 'right'
      ? 'flex-end'
      : align === 'center'
      ? 'center'
      : 'flex-start';
  const contentJustify =
    align === 'right'
      ? 'flex-end'
      : align === 'center'
      ? 'center'
      : 'flex-start';

  return (
    <Stack align={stackAlign} spacing={0.5} w="full">
      <Text
        fontSize="sm"
        fontWeight="400"
        color="#373F41"
        textAlign={align}
        w="full"
      >
        {quantityItem
          ? undefined
          : formatCurrency(resolveActivityCheckoutOnlyUnitPrice(item))}
      </Text>
      {info.row.original.is_discount || item.has_voucher_discount ? (
        hasNominalVoucher || isPromoNominalDiscount ? (
          <Box
            display="flex"
            gap="1"
            alignItems="center"
            justifyContent={contentJustify}
            flexWrap="wrap"
          >
            <Text
              color="gray.75"
              textDecoration="line-through"
              fontSize="sm"
              fontWeight="400"
            >
              {formatCurrency(originalPrice)}
            </Text>
            <Badge
              color="#FF0000"
              bgColor="#FFDADA"
              rounded="4px"
              fontSize="sm"
              fontWeight="400"
            >
              -
              {formatCurrencyNoDecimals(
                hasNominalVoucher && item.voucher_discount_amount
                  ? item.voucher_discount_amount
                  : promoNominalDiscountAmount
              )}
            </Badge>
          </Box>
        ) : (
          <Box display="flex" justifyContent={contentJustify} w="full">
            <PromoPrice
              formattedPrice={formatCurrency(originalPrice)}
              discountAmount={discountAmount}
              isActive
              isFreeGoods={false}
            />
          </Box>
        )
      ) : quantityItem ? (
        <PromoPrice
          formattedPrice={formatCurrency(info.row.original.price)}
          isFreeGoods
        />
      ) : (
        info.row.getIsSelected() === true && <div style={{ height: '20px' }} />
      )}
    </Stack>
  );
}
